import { createContext } from 'react';
import { proxy, snapshot } from 'valtio';
import type { ColumnStatistic, DocListViewSettingsInput } from '#types';
import type { DocListConfig, BaseListDocument, ColumnSpec } from './DocList.types';
import type { TableColumnType, TableValue } from '#types/System.types';
import type { FilterConflictResolver } from './createFilterCombinationGuard';
import { ACTIONS_COLUMN_ID } from './DocListTable/constants';

export type DocListTableStore = {
    sorting: Array<{ id: string; desc?: boolean }>;
    columnFilters: Array<{ id: string; value: unknown }>;
    columnVisibility: Record<string, boolean>;
    columnSizing: Record<string, number>;
    columnOrder: string[];
    columnStatistics: Record<string, ColumnStatistic>;
    sortDisabledReason: string | null;
    queryDisabledReason: string | null;
    filterDisabledColumnIds: string[];
    resolveFilterConflicts: FilterConflictResolver;
    isLoading: boolean;
};

export type DocListTableDefaults = {
    sorting: DocListTableStore['sorting'];
    filters: DocListTableStore['columnFilters'];
    visibility: DocListTableStore['columnVisibility'];
};

export function createDocListTableStore(defaults: DocListTableDefaults): DocListTableStore {
    return proxy<DocListTableStore>({
        sorting: [...defaults.sorting],
        columnFilters: [...defaults.filters],
        columnVisibility: { ...defaults.visibility },
        columnSizing: {},
        columnOrder: [],
        columnStatistics: {},
        sortDisabledReason: null,
        queryDisabledReason: null,
        filterDisabledColumnIds: [],
        resolveFilterConflicts: () => [],
        isLoading: false,
    });
}

export const DocListStoreContext = createContext<DocListTableStore>(null!);

export function resetToDefaults(store: DocListTableStore, defaults: DocListTableDefaults): void {
    store.sorting = [...defaults.sorting];
    store.columnFilters = [...defaults.filters];
    store.columnVisibility = { ...defaults.visibility };
    store.columnSizing = {};
    store.columnOrder = [];
    store.columnStatistics = {};
    store.sortDisabledReason = null;
    store.queryDisabledReason = null;
    store.filterDisabledColumnIds = [];
}

export function resetToDefaultsWithFilters(
    store: DocListTableStore,
    defaults: DocListTableDefaults,
    filters: DocListTableStore['columnFilters'],
): void {
    store.sorting = [...defaults.sorting];
    store.columnFilters = [...filters];
    store.columnVisibility = { ...defaults.visibility };
    store.columnSizing = {};
    store.columnOrder = [];
    store.columnStatistics = {};
    store.sortDisabledReason = null;
    store.queryDisabledReason = null;
    store.filterDisabledColumnIds = [];
}

export function applyViewSettings<TDoc extends BaseListDocument>(
    store: DocListTableStore,
    settings: DocListViewSettingsInput | null,
    defaults: DocListTableDefaults,
    config: DocListConfig<TDoc>,
): void {
    if (!settings) {
        resetToDefaults(store, defaults);
        return;
    }

    store.columnSizing = settings.columnSizing ?? {};
    store.columnFilters = (settings.columnFilters as DocListTableStore['columnFilters']) ?? [...defaults.filters];
    store.sorting = (settings.sorting as DocListTableStore['sorting']) ?? [...defaults.sorting];

    const enableActionsColumn = config.enableActionsColumn !== false;
    const savedColumns = Array.isArray(settings.columns)
        ? settings.columns.filter((id) => id !== ACTIONS_COLUMN_ID)
        : [];

    if (savedColumns.length > 0) {
        const existingColumnIdSet = new Set(config.columns.map((col) => col.id));
        const orderedColumns = savedColumns.filter((id) => existingColumnIdSet.has(id));

        store.columnOrder = enableActionsColumn ? [...orderedColumns, ACTIONS_COLUMN_ID] : orderedColumns;

        const requiredColumnIdSet = new Set(config.requiredColumnIds ?? []);
        const visibility: Record<string, boolean> = {};
        for (const col of config.columns) {
            visibility[col.id] = orderedColumns.includes(col.id) || requiredColumnIdSet.has(col.id);
        }
        store.columnVisibility = visibility;
    } else {
        store.columnOrder = [];
        store.columnVisibility = { ...defaults.visibility };
    }

    if (settings.version === 2 && settings.columnStatistics) {
        store.columnStatistics = { ...settings.columnStatistics };
    } else {
        store.columnStatistics = {};
    }
}

export function getViewSettingsSnapshot(
    store: DocListTableStore,
    visibleColumnIds: string[],
): DocListViewSettingsInput {
    const snap = snapshot(store);
    return {
        version: 2,
        columns: visibleColumnIds.filter((id) => id !== ACTIONS_COLUMN_ID),
        columnSizing: { ...snap.columnSizing },
        columnFilters: snap.columnFilters.map((f) => ({ id: f.id, value: f.value })),
        sorting: snap.sorting.map((s) => ({ id: s.id, desc: s.desc })),
        columnStatistics: { ...snap.columnStatistics },
    };
}

export function getExportSnapshot<TDoc extends BaseListDocument>(
    data: TDoc[],
    visibleColumns: ColumnSpec<TDoc>[],
    configId: string,
    getLabel?: (columnId: string, value: unknown) => string,
): { tableValue: TableValue; columns: TableColumnType[] } {
    const exportColumns: TableColumnType[] = visibleColumns
        .filter((col) => col.id !== ACTIONS_COLUMN_ID)
        .map((col) => ({
            dataIndex: col.id,
            description: { shortLabel: col.header },
        }));

    const applyLabel = (columnId: string, value: unknown): unknown => {
        if (!getLabel) return value;
        if (Array.isArray(value)) {
            return value
                .filter((v) => v !== null && v !== undefined && String(v) !== '')
                .map((v) => getLabel(columnId, v) || String(v))
                .join(', ');
        }
        return getLabel(columnId, value) || value;
    };

    const tableValue: TableValue = {
        rows: data.map((row, index) => {
            const value: Record<string, unknown> = {};
            for (const col of visibleColumns) {
                if (col.id === ACTIONS_COLUMN_ID) continue;
                value[col.id] = applyLabel(col.id, col.accessor(row));
            }
            return {
                key: row.docId ?? String(index),
                tableId: configId,
                value,
            };
        }),
    };

    return { tableValue, columns: exportColumns };
}

export function isDefaultView(store: DocListTableStore, defaults: DocListTableDefaults): boolean {
    const snap = snapshot(store);

    const areSortingEqual = (a: readonly { id: string; desc?: boolean }[], b: readonly { id: string; desc?: boolean }[]) => {
        if (a.length !== b.length) return false;
        return a.every((sort, idx) => {
            const other = b[idx];
            return other !== undefined && sort.id === other.id && Boolean(sort.desc) === Boolean(other.desc);
        });
    };

    const normalizeFilterValue = (value: unknown): string[] => {
        const values = Array.isArray(value)
            ? value
            : value === null || value === undefined || String(value) === ''
                ? []
                : [value];
        return values
            .filter((v) => v !== null && v !== undefined && String(v) !== '')
            .map((v) => String(v))
            .sort();
    };

    const areFiltersEqual = (
        a: readonly { id: string; value: unknown }[],
        b: readonly { id: string; value: unknown }[],
    ) => {
        if (a.length !== b.length) return false;
        const aMap = new Map(a.map((f) => [f.id, normalizeFilterValue(f.value)]));
        const bMap = new Map(b.map((f) => [f.id, normalizeFilterValue(f.value)]));
        if (aMap.size !== bMap.size) return false;
        for (const [id, aValue] of aMap.entries()) {
            const bValue = bMap.get(id);
            if (!bValue) return false;
            if (aValue.length !== bValue.length) return false;
            if (!aValue.every((v, i) => v === bValue[i])) return false;
        }
        return true;
    };

    const areRecordsEqual = (a: Record<string, unknown>, b: Record<string, unknown>) => {
        const aKeys = Object.keys(a).sort();
        const bKeys = Object.keys(b).sort();
        if (aKeys.length !== bKeys.length) return false;
        if (!aKeys.every((key, idx) => key === bKeys[idx])) return false;
        return aKeys.every((key) => a[key] === b[key]);
    };

    return (
        areSortingEqual(snap.sorting, defaults.sorting) &&
        areFiltersEqual(snap.columnFilters, defaults.filters) &&
        areRecordsEqual(snap.columnVisibility, defaults.visibility) &&
        areRecordsEqual(snap.columnSizing, {}) &&
        snap.columnOrder.length === 0
    );
}
